import { useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import useAppModal from 'hooks/useAppModal';
import useAppSelector from 'hooks/useAppSelector';
import useCustomTemplateAddEditModal from './modal/useCustomTemplateAddEditModal';
import { CUSTOM_TEMPLATE_ADD_EDIT_MODAL_ID } from './modal/CustomTemplateAddEditModal.constants';

/**
 * Route component that bridges URL navigation with modal opening for custom template add/edit flows.
 *
 * Watches URL parameters (`key` param: "add" for new templates, or a UID for editing) and opens
 * the appropriate modal. Uses URL-based navigation instead of direct modal opening to enable
 * proper dirty checking - the form's dirty checker can intercept browser navigation events
 * (back button, URL changes) and warn users about unsaved changes.
 *
 * On unmount, closes the modal to ensure proper cleanup.
 */
const CustomTemplatesTabModal = () => {
  const hasInit = useRef(false);
  const { key } = useParams();
  const activeModals = useAppSelector((state) => state.modal.activeModals);
  const { closeModal } = useAppModal();
  const { openAddCustomTemplateModal, openEditCustomTemplateModal } =
    useCustomTemplateAddEditModal();

  useEffect(() => {
    if (
      key &&
      !activeModals[CUSTOM_TEMPLATE_ADD_EDIT_MODAL_ID] &&
      !hasInit.current
    ) {
      if (key === 'add') {
        openAddCustomTemplateModal();
      } else {
        openEditCustomTemplateModal({ customTemplateUid: key });
      }
      hasInit.current = true;
    }
  }, [activeModals, key]);

  useEffect(() => {
    return () => {
      if (hasInit.current) {
        closeModal(CUSTOM_TEMPLATE_ADD_EDIT_MODAL_ID);

        hasInit.current = false;
      }
    };
  }, []);

  return null;
};

export default CustomTemplatesTabModal;
